import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiShieldCheck, HiXMark, HiEye, HiEyeSlash } from 'react-icons/hi2';
import { verifyPasswordResetOtp } from '../../services/settingsService';
import toast from 'react-hot-toast';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

/**
 * Popover 2: Password Reset Form Prompt
 * This is the second step in the two-step password reset flow.
 * User sets their new password after OTP verification.
 */
export default function PasswordResetFormPrompt({
  email,
  otpRequestId,
  otpCode,
  open,
  onSuccess,
  onCancel,
}) {
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSubmit = async e => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Normalize email before sending to match backend expectation
      const normalizedEmail = email?.toLowerCase().trim();

      const res = await verifyPasswordResetOtp({
        otpRequestId,
        email: normalizedEmail,
        otp: otpCode,
        newPassword,
      });

      if (res.success) {
        toast.success('Password reset successful. Please log in.');
        if (onSuccess) onSuccess();
      } else {
        setError('Password reset failed. Please try again.');
      }
    } catch (err) {
      // Handle specific error codes from backend
      let errorMessage = 'Password reset failed. Please try again.';

      if (err?.code === 'deadline-exceeded' || err?.message?.includes('expired')) {
        errorMessage = 'OTP has expired. Please start over and request a new code.';
      } else if (err?.code === 'invalid-argument' && err?.message?.includes('Password')) {
        errorMessage = err.message;
      } else if (err?.code === 'invalid-argument' || err?.message?.includes('Invalid')) {
        errorMessage = 'Invalid or expired OTP. Please start over.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = err?.message || 'OTP has already been used or is invalid.';
      } else if (err?.code === 'resource-exhausted') {
        errorMessage = 'Too many incorrect attempts. Please start over.';
      } else if (err?.code === 'not-found') {
        errorMessage = 'OTP request not found. Please start over.';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && onCancel) onCancel();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="password-reset-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      >
        <motion.div
          key="password-reset-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>

          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-600 text-white">
              <HiShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Set New Password</h2>
              <p className="text-xs text-gray-600">
                Create a new password for <span className="font-medium">{email}</span>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 pr-10 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                  placeholder="Enter new password"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <HiEyeSlash className="h-5 w-5" />
                  ) : (
                    <HiEye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 pr-10 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                  placeholder="Confirm new password"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <HiEyeSlash className="h-5 w-5" />
                  ) : (
                    <HiEye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}
            <p className="text-xs text-gray-500">Password must be at least 8 characters long.</p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  loading ||
                  !newPassword ||
                  newPassword.length < 8 ||
                  newPassword !== confirmPassword
                }
                className="rounded-lg bg-green-600 px-4 py-2 text-xs font-semibold text-white shadow-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
